import Dexie, { type EntityTable } from 'dexie';
import type { WidgetConfig } from '@/types/widget';
import type { ColumnOverrides } from '@/types/dataset';
import type { ColumnTransform } from '@/types/query';

/**
 * Dashboard mode — Phase 9 (D-020). Each (dataset, mode) pair owns its
 * own widgets, layout, and pinned filters. Switching modes swaps the
 * dashboard wholesale; the two never share state.
 */
export type DashboardMode = 'normal' | 'compare';

/** Granularity choices for compare mode (D-020). */
export type CompareGranularity =
  | 'monthly'
  | 'quarterly'
  | 'semiannual'
  | 'yearly';

// ---------------------------------------------------------------------------
// Schema types
// ---------------------------------------------------------------------------

/**
 * Persisted widget layout per dataset + mode, per zone. The PK is a
 * synthetic `key` string (`${datasetId}|${mode}`) introduced in v7
 * because Dexie's typed `EntityTable` only models scalar keys cleanly.
 * Use {@link layoutKey} to derive it.
 */
export type PersistedLayout = {
  /** Primary key. `${datasetId}|${mode}`. */
  key: string;
  datasetId: string;
  mode: DashboardMode;
  /** Serialized react-grid-layout Layouts object (JSON string). */
  layouts: string;
  /** Timestamp of last update. */
  updatedAt: number;
};

/** Build the synthetic PK for layouts / pinned-filter rows (D-020). */
export function layoutKey(datasetId: string, mode: DashboardMode): string {
  return `${datasetId}|${mode}`;
}

/** Generic key-value preferences (theme, locale, etc.). */
export type Preference = {
  /** Preference key (e.g. 'theme'). */
  key: string;
  /** Preference value (serialized as string). */
  value: string;
};

/**
 * Persisted pinned-filter column selection for a dataset + mode. PK is
 * the synthetic `key` (D-020) — same scheme as {@link PersistedLayout}.
 */
export type PinnedFiltersEntry = {
  /** Primary key. `${datasetId}|${mode}`. */
  key: string;
  datasetId: string;
  mode: DashboardMode;
  /** Ordered list of column names the user has pinned as filter slots. */
  columns: string[];
  /** Timestamp of last update. */
  updatedAt: number;
};

/**
 * One side of a comparison. `sub` semantics depend on granularity:
 *   - `monthly`     → 1..12   (month number)
 *   - `quarterly`   → 1..4    (quarter number)
 *   - `semiannual`  → 1..2    (half-year number)
 *   - `yearly`      → undefined (year alone is the period)
 */
export type ComparePeriod = {
  year: number;
  sub?: number;
};

/**
 * Compare-mode configuration per dataset. Only consulted while
 * `mode === 'compare'`. Single row per dataset. Per the user's
 * Phase-9 spec the two periods are picked explicitly (not derived
 * from the dataset's max date) so the comparison is reproducible
 * across reloads and across users sharing the same export.
 */
export type CompareConfigEntry = {
  /** Primary key: dataset id. */
  datasetId: string;
  /** DATE / TIMESTAMP column the comparison is anchored to. */
  dateColumn: string;
  granularity: CompareGranularity;
  periodA: ComparePeriod;
  periodB: ComparePeriod;
  updatedAt: number;
};

/** Persisted manual column-type overrides for a dataset. */
export type ColumnOverridesEntry = {
  /** Primary key: dataset id. */
  datasetId: string;
  /** Column name → override. Empty-object entries are treated as "clear all". */
  overrides: ColumnOverrides;
  /** Timestamp of last update. */
  updatedAt: number;
};

/** Persisted per-column value groupings (synonym maps + numeric bins). */
export type ValueGroupingsEntry = {
  /** Primary key: dataset id. */
  datasetId: string;
  /** Column name → transform. Empty-object entries are treated as "clear all". */
  groupings: Record<string, ColumnTransform>;
  /** Timestamp of last update. */
  updatedAt: number;
};

// ---------------------------------------------------------------------------
// Database
// ---------------------------------------------------------------------------

/**
 * Dexie database for persisting user configs (layout, theme, widgets).
 * Data never leaves the browser. Uses IndexedDB only — no localStorage.
 *
 * v2: layouts split into `kpiLayouts` and `chartLayouts` per zone.
 * v3: adds `widgets` table (user-editable widget configs, Phase 6 Step 5).
 * v4: adds `pinnedFilters` table (user-pinned filter columns, Phase 6).
 * v5: adds `columnOverrides` table (manual column-type overrides, Phase 6).
 * v6: adds `valueGroupings` table (synonym maps + numeric bins, Phase 6).
 * v7: comparison mode (Phase 9, D-020). Adds `compareConfigs` table and
 *     stamps every existing widget row with `mode = 'normal'` via a
 *     `[datasetId+mode]` compound index. The original v7 attempted to
 *     change the primary key of `kpiLayouts` / `chartLayouts` /
 *     `pinnedFilters` from `datasetId` to a synthetic `key`, but
 *     IndexedDB does not allow keyPath changes on existing object stores
 *     (Dexie throws `UpgradeError: Not yet support for changing primary
 *     key`). v7 was rolled back to keep PKs unchanged; the per-mode key
 *     migration moved into v8–v11 using the canonical Dexie temp-backup
 *     drop-recreate pattern.
 * v8–v11: per-mode synthetic-key migration for layouts and pinned filters.
 *     v8 backs the three tables up into temp stores, v9 drops them, v10
 *     recreates them with the new PK and restores from backup, v11
 *     cleans the temp stores. Fresh installs jump directly to v11's
 *     end-state so no upgrade callbacks run.
 * The legacy `layouts` table was dropped in v2 — v1 data is discarded
 * on upgrade (acceptable: no production data yet; Phase 7 introduces
 * proper migration).
 */
class ConfigDB extends Dexie {
  kpiLayouts!: EntityTable<PersistedLayout, 'key'>;
  chartLayouts!: EntityTable<PersistedLayout, 'key'>;
  preferences!: EntityTable<Preference, 'key'>;
  widgets!: EntityTable<WidgetConfig, 'id'>;
  pinnedFilters!: EntityTable<PinnedFiltersEntry, 'key'>;
  columnOverrides!: EntityTable<ColumnOverridesEntry, 'datasetId'>;
  valueGroupings!: EntityTable<ValueGroupingsEntry, 'datasetId'>;
  compareConfigs!: EntityTable<CompareConfigEntry, 'datasetId'>;

  constructor() {
    super('ExcelToChatsConfigDB');
    this.version(1).stores({
      layouts: 'datasetId',
      preferences: 'key',
    });
    this.version(2).stores({
      layouts: null,
      kpiLayouts: 'datasetId',
      chartLayouts: 'datasetId',
      preferences: 'key',
    });
    this.version(3).stores({
      kpiLayouts: 'datasetId',
      chartLayouts: 'datasetId',
      preferences: 'key',
      widgets: 'id, datasetId',
    });
    this.version(4).stores({
      kpiLayouts: 'datasetId',
      chartLayouts: 'datasetId',
      preferences: 'key',
      widgets: 'id, datasetId',
      pinnedFilters: 'datasetId',
    });
    this.version(5).stores({
      kpiLayouts: 'datasetId',
      chartLayouts: 'datasetId',
      preferences: 'key',
      widgets: 'id, datasetId',
      pinnedFilters: 'datasetId',
      columnOverrides: 'datasetId',
    });
    this.version(6).stores({
      kpiLayouts: 'datasetId',
      chartLayouts: 'datasetId',
      preferences: 'key',
      widgets: 'id, datasetId',
      pinnedFilters: 'datasetId',
      columnOverrides: 'datasetId',
      valueGroupings: 'datasetId',
    });
    // v7 — comparison mode (D-020). Adds `compareConfigs` and the
    // [datasetId+mode] index on widgets. PKs of layouts / pinnedFilters
    // stay as `datasetId`; the synthetic-key migration moved to v8–v11
    // because IndexedDB rejects keyPath changes on existing stores.
    this.version(7)
      .stores({
        kpiLayouts: 'datasetId',
        chartLayouts: 'datasetId',
        preferences: 'key',
        widgets: 'id, datasetId, [datasetId+mode]',
        pinnedFilters: 'datasetId',
        columnOverrides: 'datasetId',
        valueGroupings: 'datasetId',
        compareConfigs: 'datasetId',
      })
      .upgrade(async (tx) => {
        await tx
          .table('widgets')
          .toCollection()
          .modify((w: { mode?: DashboardMode }) => {
            if (typeof w.mode !== 'string') w.mode = 'normal';
          });
      });

    // v8 — back layout / pinned data into temp stores so v10 can restore
    // it after the PK swap. Dexie can't change a keyPath in place, so
    // the canonical pattern is backup → drop → recreate → restore.
    this.version(8)
      .stores({
        _v8_kpiBackup: '++_id',
        _v8_chartBackup: '++_id',
        _v8_pinnedBackup: '++_id',
      })
      .upgrade(async (tx) => {
        const backup = async (
          src: 'kpiLayouts' | 'chartLayouts' | 'pinnedFilters',
          dst: '_v8_kpiBackup' | '_v8_chartBackup' | '_v8_pinnedBackup',
        ): Promise<void> => {
          try {
            const rows = await tx.table(src).toArray();
            if (rows.length > 0) await tx.table(dst).bulkAdd(rows);
          } catch (err) {
            // Source may be empty / missing on a fresh-install pre-v8 → skip.
            // Logged so a real backup failure shows up in production debugging.
            console.warn(`configDb v8 backup of ${src} skipped`, err);
          }
        };
        await backup('kpiLayouts', '_v8_kpiBackup');
        await backup('chartLayouts', '_v8_chartBackup');
        await backup('pinnedFilters', '_v8_pinnedBackup');
      });

    // v9 — drop the three tables so v10 can recreate them with the new PK.
    this.version(9).stores({
      kpiLayouts: null,
      chartLayouts: null,
      pinnedFilters: null,
    });

    // v10 — recreate with the per-mode synthetic key + restore from backup.
    // Restored rows are stamped with `mode = 'normal'` so they re-attach
    // to the normal-mode dashboard, matching the original v7 intent.
    this.version(10)
      .stores({
        kpiLayouts: 'key, datasetId, [datasetId+mode]',
        chartLayouts: 'key, datasetId, [datasetId+mode]',
        pinnedFilters: 'key, datasetId, [datasetId+mode]',
      })
      .upgrade(async (tx) => {
        const restore = async (
          backup: '_v8_kpiBackup' | '_v8_chartBackup' | '_v8_pinnedBackup',
          target: 'kpiLayouts' | 'chartLayouts' | 'pinnedFilters',
        ): Promise<void> => {
          try {
            const rows = await tx.table(backup).toArray();
            for (const row of rows) {
              const r = row as Record<string, unknown> & { _id?: number };
              delete r._id;
              const datasetId = String(r.datasetId ?? '');
              if (!datasetId) continue;
              const mode: DashboardMode =
                r.mode === 'compare' ? 'compare' : 'normal';
              r.mode = mode;
              r.key = layoutKey(datasetId, mode);
              await tx.table(target).put(r);
            }
          } catch (err) {
            // Best effort — don't block the upgrade on restore failure.
            // Logged so a real restore failure (lost layouts) shows up
            // in production debugging instead of disappearing.
            console.warn(`configDb v10 restore from ${backup} failed`, err);
          }
        };
        await restore('_v8_kpiBackup', 'kpiLayouts');
        await restore('_v8_chartBackup', 'chartLayouts');
        await restore('_v8_pinnedBackup', 'pinnedFilters');
      });

    // v11 — drop the temp backup stores now that data has been restored.
    this.version(11).stores({
      _v8_kpiBackup: null,
      _v8_chartBackup: null,
      _v8_pinnedBackup: null,
    });
  }
}

/** Singleton database instance. */
export const configDb = new ConfigDB();

/** Zone identifier used by the persistence hook. */
export type LayoutZone = 'kpi' | 'chart';

/** Resolve the Dexie table for a given zone. */
export function layoutTable(
  zone: LayoutZone,
): EntityTable<PersistedLayout, 'key'> {
  return zone === 'kpi' ? configDb.kpiLayouts : configDb.chartLayouts;
}
